// src/App.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import GlobalStyle from './GlobalStyle';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import HomePage from './pages/HomePage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ProfilePage from './pages/ProfilePage';
import AutomatedScanPage from './pages/ScanPageAutomated';
import ScanPageInternal from './pages/ScanPageInternal';
import ManualScanPage from './pages/ScanPageManual';
import PreEngagementForm from './pages/PreEngagementForm';
import TimelinePage from './pages/TimelinePage';
import { useLoginContext } from './context/LoginContext';
import ReportPage from './pages/ReportPage';
import AutomatedReportPage from './pages/AutomatedReportPage';
import AdvancedReportPage from './pages/AdvancedReportPage';
import NotFoundPage from './pages/404';
import IntegrationPage from './pages/IntegrationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import LoginWithOtpPage from './pages/VerifyLoginOtpPage';
import VerifyLoginOtpPage from './pages/VerifyLoginOtpPage';
import ContinueWithOtpPage from './pages/ContinueWithOtpPage';
import AssetsPage from './pages/AssetsPage';
import ScanConfiguration from './pages/scanProfile';
import Cookies from 'js-cookie';
import ReportPdfPage from './pages/ReportPdfPage';
import AdvancedScanReportPage from './pages/AdvancedScanReportPage';
import AddAssetsWeb from './pages/AddAssetsWeb';
import AddAssetsApi from './pages/AddAssetsApi';
import AddAssetsNetwork from './pages/AddAssetsNetwork';
import AddAssetsFirewall from './pages/AddAssetsFirewall';
import EditAssetsWeb from './pages/EditAssetsWeb';
import EditAssetsApi from './pages/EditAssetsApi';
import EditAssetsNetwork from './pages/EditAssetsNetwork';
import EditAssetsFirewall from './pages/EditAssetsFirewall';
import UpgradePlanPage from './pages/UpgradePlanPage';
import TeamsPage from './pages/TeamsPage';
import ReconnaissancePage from './pages/ReconnaissancePage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import DummyUnsubscribe from './pages/Unsubscript';
const AppContainer = styled.div`
  display: flex;
`;

const MainContentWrapper = styled.div`
  width: 100%;
  padding-top: ${({ authPage }) => (authPage ? '0' : '51px')};
  min-height: 100vh;
  box-sizing: border-box;
  transition: margin-left 0.3s ease;
  padding-inline: ${({ authPage }) => (authPage ? '0' : '20px')};

  @media screen and (max-width: 768px) {
    margin-left: ${({ open, authPage }) => (authPage ? '0' : (open ? '250px' : '0'))};
  }
`;

const App = () => {
  const { isAuthenticated, login } = useLoginContext();
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => Cookies.get('theme') || 'light');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const location = useLocation();

  // Enhanced path sanitization function
  const sanitizePath = (path) => {
    return path
      .replace(/["']/g, '')       // Remove quotes
      .replace(/\/+$/, '')        // Remove trailing slashes
      .replace(/[^\w\-./]/g, ''); // Remove special chars
  };

  // Define protected routes with their matching types
  const protectedRoutes = [
    { path: '/', exact: true },
    { path: '/report', prefix: true },
    { path: '/settings', prefix: true },
    { path: '/profile', exact: true },
    { path: '/autoscan', exact: true },
    { path: '/automated-reports', prefix: true },
    { path: '/advanced-reports', prefix: true },
    { path: '/advancedscan', prefix: true },
    { path: '/vulnerability-assessment', prefix: true },
    { path: '/pre-engagement-form', prefix: true },
    { path: '/scan-timeline', exact: true },
    { path: '/advanced-scan-report', prefix: true },
    { path: '/integration', prefix: true },
    { path: '/add-assets', prefix: true },
    { path: '/edit-assets', prefix: true },
    { path: '/scan-configure', exact: true },
    { path: '/export', prefix: true },
    { path: '/upgrade-plan', exact: true },
    { path: '/teams', exact: true },
    { path: '/reconnaissance', exact: true }
  ];

  // Routes that do NOT show the layout (auth pages)
  const authRoutes = ['/login', '/signup', '/forgot-password', '/continue-otp', '/verify-otp', '/payment/success', '/payment/failed'];

  // Check if current path is an auth page
  const isAuthPage = authRoutes.includes(sanitizePath(location.pathname));

  // Enhanced protected route check with sanitization
  const isProtectedPage = protectedRoutes.some(route => {
    const cleanPath = sanitizePath(location.pathname);
    const cleanRoute = sanitizePath(route.path);

    if (route.exact) {
      return cleanPath === cleanRoute;
    }
    if (route.prefix) {
      return cleanPath === cleanRoute || cleanPath.startsWith(cleanRoute + '/');
    }
    return false;
  });

  // Hide layout for auth pages or non-protected pages
  const hideLayout = isAuthPage || !isProtectedPage;

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      login(token); // Restore login state from token
    }
    setLoading(false);
  }, [login]);

  // Update the document body attribute whenever theme changes
  useEffect(() => {
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  // Toggle the theme and save it to a cookie for persistence
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    Cookies.set('theme', newTheme, { expires: 365 });
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Handle sidebar toggle from Sidebar component
  const handleSidebarToggle = (expanded) => {
    setIsSidebarExpanded(expanded);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <GlobalStyle />
      <AppContainer>
        {!hideLayout && (
          <Sidebar 
            toggleSidebar={toggleSidebar} 
            open={sidebarOpen} 
            theme={theme} 
            onToggle={handleSidebarToggle}
          />
        )}
        <MainContentWrapper open={sidebarOpen} authPage={hideLayout}>
          {!hideLayout && (
            <Navbar 
              toggleTheme={toggleTheme} 
              theme={theme} 
              isSidebarExpanded={isSidebarExpanded}
            />
          )}
          <Routes>
            {/* Protected Routes */}
            <Route
              path="/"
              element={isAuthenticated ? <HomePage theme={theme}/> : <Navigate to="/login" />}
            />
            <Route
              path="/report/:scanType/:scanId"
              element={isAuthenticated ? <AnalyticsPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/settings"
              element={isAuthenticated ? <SettingsPage theme={theme}/> : <Navigate to="/login" />}
            />
            <Route
              path="/profile"
              element={isAuthenticated ? <ProfilePage /> : <Navigate to="/login" />}
            />
            <Route
              path="/autoscan"
              element={isAuthenticated ? <AutomatedScanPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/automated-reports"
              element={isAuthenticated ? <AutomatedReportPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/advanced-reports"
              element={isAuthenticated ? <AdvancedReportPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/reports"
              element={isAuthenticated ? <ReportPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/integration"
              element={isAuthenticated ? <IntegrationPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/advancedscan"
              element={isAuthenticated ? <ManualScanPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/vulnerability-assessment"
              element={isAuthenticated ? <ScanPageInternal /> : <Navigate to="/login" />}
            />
            <Route
              path="/pre-engagement-form/:scanType"
              element={isAuthenticated ? <PreEngagementForm /> : <Navigate to="/login" />}
            />
            <Route
              path="/scan-timeline"
              element={isAuthenticated ? <TimelinePage /> : <Navigate to="/login" />}
            />
            <Route
              path="/scan-configure"
              element={isAuthenticated ? <ScanConfiguration /> : <Navigate to="/login" />}
            />
            <Route
              path="/add-assets"
              element={isAuthenticated ? <AssetsPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/add-assets/web"
              element={isAuthenticated ? <AddAssetsWeb /> : <Navigate to="/login" />}
            />
            <Route
              path="/add-assets/api"
              element={isAuthenticated ? <AddAssetsApi /> : <Navigate to="/login" />}
            />
            <Route
              path="/add-assets/network"
              element={isAuthenticated ? <AddAssetsNetwork /> : <Navigate to="/login" />}
            />
            <Route
              path="/add-assets/firewall"
              element={isAuthenticated ? <AddAssetsFirewall /> : <Navigate to="/login" />}
            />
            <Route
              path="/edit-assets/web"
              element={isAuthenticated ? <EditAssetsWeb /> : <Navigate to="/login" />}
            />
            <Route
              path="/edit-assets/api"
              element={isAuthenticated ? <EditAssetsApi /> : <Navigate to="/login" />}
            />
            <Route
              path="/edit-assets/network"
              element={isAuthenticated ? <EditAssetsNetwork /> : <Navigate to="/login" />}
            />
            <Route
              path="/edit-assets/firewall"
              element={isAuthenticated ? <EditAssetsFirewall /> : <Navigate to="/login" />}
            />
            <Route
              path="/export/:scanType/:scanId"
              element={isAuthenticated ? <ReportPdfPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/advanced-scan-report/:scan_type/:scan_id/"
              element={isAuthenticated ? <AdvancedScanReportPage /> : <Navigate to="/login" />} 
            />
            <Route
              path="/upgrade-plan"
              element={isAuthenticated ? <UpgradePlanPage /> : <Navigate to="/login" />} 
            />
            <Route
              path="/teams"
              element={isAuthenticated ? <TeamsPage /> : <Navigate to="/login" />}
            />
            <Route
              path="/reconnaissance"
              element={isAuthenticated ? <ReconnaissancePage /> : <Navigate to="/login" />}
            />

              {/* Payment Result Pages (Public) */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/failed" element={<PaymentFailed />} />

            {/* Auth Routes */}
            <Route
              path="/login"
              element={!isAuthenticated ? <LoginPage /> : <Navigate to="/" />}
            />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/continue-otp" element={<ContinueWithOtpPage />} />
            <Route path="/verify-otp" element={<VerifyLoginOtpPage />} />
            <Route path='/unsubscribe' element={<DummyUnsubscribe />} />
            {/* Not Found Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </MainContentWrapper>
      </AppContainer>
    </div>
  );
};

export default App;
